import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { apiService } from '../../../services/api.service';
import { toast } from 'react-toastify';
import { GreenTick, UploadIcon } from '../../../assets';
import { pad } from '../../files/files.page';

interface IProps {
  onAdded: () => void;
  highlight?: boolean;
}

const cn = createCn('speakers', classes);

function dateInputValue(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const AddSpeakerPanel: React.FC<IProps> = ({ onAdded, highlight }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVoiceFile(e.target.files?.[0] ?? null);
  };

  const handleAdd = async () => {
    if (!name.trim() || !voiceFile || submitting) return;
    setSubmitting(true);
    try {
      await apiService.createSpeaker(name.trim(), position.trim(), voiceFile);
      setName('');
      setPosition('');
      setVoiceFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onAdded();
      toast(
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <GreenTick />
          {t('speakers.success')}
        </div>,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('speakers.add-error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={cn('add-panel', { highlight: Boolean(highlight) })}>
      <div className={cn('add-field')}>
        <label htmlFor="speaker-name">{t('speakers.fullname')}</label>
        <input
          id="speaker-name"
          type="text"
          placeholder={t('speakers.fullname')}
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>
      <div className={cn('add-field')}>
        <label htmlFor="speaker-position">{t('speakers.position')}</label>
        <input
          id="speaker-position"
          type="text"
          placeholder={t('speakers.position')}
          value={position}
          onChange={e => setPosition(e.target.value)}
          list="speaker-positions"
        />
        <datalist id="speaker-positions">
          <option value="ML Engineer" />
          <option value="Director" />
          <option value="Data analyst" />
        </datalist>
      </div>
      <div className={cn('add-field')}>
        <label htmlFor="speaker-date">{t('speakers.create-date')}</label>
        <input
          id="speaker-date"
          type="date"
          value={dateInputValue()}
          readOnly
          tabIndex={-1}
        />
      </div>
      <div className={cn('add-field')}>
        <label>{t('speakers.voice')}</label>
        <label className={cn('upload-box')}>
          <UploadIcon />
          <span>{voiceFile ? voiceFile.name : t('speakers.upload-file')}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.mp4,.webm,.m4v"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
        </label>
      </div>
      <button
        type="button"
        className={cn('add-submit')}
        onClick={handleAdd}
        disabled={!name.trim() || !voiceFile || submitting}
      >
        {submitting ? t('speakers.saving') : t('action.add')}
      </button>
    </div>
  );
};

export default AddSpeakerPanel;
